import {
  Alert,
  Box,
  CircularProgress,
  Typography,
  useTheme,
} from "@mui/material";
import { Link, Navigate, useParams } from "react-router";

import { Invocations } from "@/Actions/components";
import { useAuth } from "@/providers";
import { PageBreadcrumbs, PageLayout } from "@/shared/components";
import { TriggerDispatchesForTrigger } from "@/shared/components/TriggerDispatches";
import { type OrgRecord } from "@/shared/domain/orgs";
import { type TriggerV2Record } from "@/shared/domain/triggers";
import { useTriggerV2, useTriggerV2ById } from "@/shared/domain/triggers/hooks";

import { TriggerDetailTabBar } from "./TriggerDetailTabBar";
import {
  LEGACY_HISTORY_SEGMENT,
  triggerDetailPath,
  triggerDetailTabs,
  type TriggerDetailTab,
  type TriggerDetailTabName,
} from "./triggerDetailTabs";
import { TriggerOverview } from "./TriggerOverview";
import { TriggerThreads } from "./TriggerThreads";

export function Trigger() {
  const params = useParams<{ triggerName: string; "*": string }>();
  const triggerName = params.triggerName;
  // The detail route is registered as `/triggers/:triggerName/*`, so the splat
  // is the tab segment: empty for the index tab.
  const tabSegment = params["*"] ?? "";

  const theme = useTheme();
  const { currentOrganization } = useAuth();

  // A `roboto://trigger/<id>` link lands here with the id where the name
  // goes; resolve it and move to the name URL the page is addressed by.
  const isTriggerId = triggerName?.startsWith("tr_") ?? false;
  const byIdQuery = useTriggerV2ById(isTriggerId ? triggerName : undefined);
  // The unified read (GET /v1/triggers/<name>) serves every trigger: v2 rows
  // natively, legacy v1 rows translated forward, schedule-fired ones included.
  const triggerV2Query = useTriggerV2(isTriggerId ? undefined : triggerName);
  const triggerV2 = triggerV2Query.data;

  const Breadcrumbs = (
    <PageBreadcrumbs>
      <Link to="/triggers">Triggers</Link>
      <span>{triggerName}</span>
    </PageBreadcrumbs>
  );

  if (isTriggerId && byIdQuery.data !== undefined) {
    return (
      <Navigate
        to={triggerDetailPath(
          byIdQuery.data.name,
          tabSegment === "" ? "details" : (tabSegment as TriggerDetailTabName),
        )}
        replace
      />
    );
  }

  if (triggerV2Query.isLoading || byIdQuery.isLoading) {
    return (
      <PageLayout title={"Roboto - Trigger"} headerContentLeft={Breadcrumbs}>
        <CircularProgress />
      </PageLayout>
    );
  }

  let errorDisplay = null;
  const loadError = triggerV2Query.error ?? byIdQuery.error;
  if (loadError !== null) {
    errorDisplay = (
      <Box sx={{ mt: theme.spacing(2), mb: theme.spacing(1) }}>
        <Alert severity="error">{loadError.message}</Alert>
      </Box>
    );
  }

  if (!currentOrganization) {
    return (
      <PageLayout title={"Roboto - Trigger"} headerContentLeft={Breadcrumbs}>
        <Typography color={theme.palette.error.main} variant="h4">
          No organization selected
        </Typography>
      </PageLayout>
    );
  }

  const tabs: readonly TriggerDetailTab[] = triggerV2
    ? triggerDetailTabs(triggerV2)
    : [];

  if (triggerName !== undefined && tabSegment === LEGACY_HISTORY_SEGMENT) {
    return <Navigate to={triggerDetailPath(triggerName, "history")} replace />;
  }
  // A tab with nothing to show, or one this page does not have: back to the
  // index tab rather than a greyed or empty page.
  if (
    tabs.length > 0 &&
    triggerName !== undefined &&
    !tabs.some(
      (tab) =>
        tab.unavailableReason === null &&
        (tab.name === tabSegment ||
          (tab.name === "details" && tabSegment === "")),
    )
  ) {
    return <Navigate to={triggerDetailPath(triggerName, "details")} replace />;
  }

  const activeTab: TriggerDetailTabName =
    tabSegment === "" ? "details" : (tabSegment as TriggerDetailTabName);

  return (
    <PageLayout title={"Roboto - Trigger"} headerContentLeft={Breadcrumbs}>
      {errorDisplay}
      {triggerV2 && triggerName !== undefined && (
        <>
          <TriggerDetailTabBar
            triggerName={triggerName}
            tabs={tabs}
            activeTab={activeTab}
          />
          <Box
            sx={{
              padding: theme.spacing(1, 0),
              paddingBottom: theme.spacing(1),
            }}
          >
            <V2TabContent
              record={triggerV2}
              tab={activeTab}
              currentOrg={currentOrganization}
            />
          </Box>
        </>
      )}
    </PageLayout>
  );
}

function V2TabContent({
  record,
  tab,
  currentOrg,
}: {
  record: TriggerV2Record;
  tab: TriggerDetailTabName;
  currentOrg: OrgRecord;
}) {
  switch (tab) {
    case "details":
      return <TriggerOverview record={record} />;
    case "history":
      return (
        <TriggerDispatchesForTrigger
          triggerId={record.trigger_id}
          targets={record.targets}
        />
      );
    case "threads":
      return <TriggerThreads triggerId={record.trigger_id} />;
    case "invocations":
      return (
        <Invocations triggerId={record.trigger_id} currentOrg={currentOrg} />
      );
  }
}
